/*
  EBOLT masthead behaviour (sections/ebolt-masthead.liquid).

  - Tab row: each tab is a disclosure button for its mega panel. Click
    toggles; a mouse pointer opens on hover (short intent delay) and closes
    when it leaves the tab row; Escape closes and returns focus; one panel
    at a time; a click outside closes.
  - Category panel: the left rail is a vertical tablist. Hover, click or
    focus selects a category; arrow keys, Home and End move between them.
  - Drawer: a modal <dialog> for phones and tablets.
  - Writes --ebm-strip (yellow strip height, so the header group sticks
    with only the strip scrolled away), --header-height and
    --eb-sticky-top (where sticky page parts should start).
*/
(() => {
  const root = document.querySelector('[data-ebm]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';

  const group = document.getElementById('header-group');
  const tabs = [...root.querySelectorAll('[data-ebm-tab]')];
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  let openTab = null;
  let hoverTimer = 0;
  let leaveTimer = 0;

  const panelOf = (tab) => document.getElementById(tab.getAttribute('aria-controls'));

  function close(tab, { focus = false } = {}) {
    if (!tab) return;
    tab.setAttribute('aria-expanded', 'false');
    const panel = panelOf(tab);
    if (panel) panel.hidden = true;
    if (openTab === tab) openTab = null;
    if (focus) tab.focus();
  }

  function open(tab) {
    if (openTab === tab) return;
    const switching = !!openTab;
    close(openTab);
    const panel = panelOf(tab);
    if (!panel) return;
    tab.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    panel.classList.remove('is-entering');
    // Moving between tabs swaps panels in place; only a panel opening from closed drops in.
    if (!switching) {
      void panel.offsetWidth;
      panel.classList.add('is-entering');
    }
    openTab = tab;
  }

  for (const tab of tabs) {
    tab.addEventListener('click', () => {
      clearTimeout(hoverTimer);
      if (openTab === tab) close(tab);
      else open(tab);
    });

    const item = tab.closest('.ebm-tabs__item');
    item.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || !finePointer.matches) return;
      clearTimeout(leaveTimer);
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => open(tab), openTab ? 0 : 110);
    });
    item.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(hoverTimer);
    });
  }

  const tabRow = root.querySelector('.ebm-tabs');
  if (tabRow) {
    tabRow.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse' || !openTab) return;
      clearTimeout(leaveTimer);
      leaveTimer = setTimeout(() => close(openTab), 220);
    });
    tabRow.addEventListener('pointerenter', () => clearTimeout(leaveTimer));
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openTab) close(openTab, { focus: true });
  });
  document.addEventListener('click', (e) => {
    if (openTab && !root.contains(e.target)) close(openTab);
  });
  // Keyboard users leaving the tab row close the open panel.
  root.addEventListener('focusout', (e) => {
    if (!openTab || !tabRow) return;
    if (e.relatedTarget && !tabRow.contains(e.relatedTarget)) close(openTab);
  });

  // Category rail: vertical tabs inside the category panel.
  for (const rail of root.querySelectorAll('[data-ebm-rail]')) {
    const items = [...rail.querySelectorAll('[role="tab"]')];
    let railTimer = 0;
    const select = (item, { focus = false } = {}) => {
      for (const it of items) {
        const on = it === item;
        it.setAttribute('aria-selected', String(on));
        it.tabIndex = on ? 0 : -1;
        const pane = document.getElementById(it.getAttribute('aria-controls'));
        if (pane) pane.hidden = !on;
      }
      if (focus) item.focus();
    };
    items.forEach((item, i) => {
      item.addEventListener('click', () => select(item));
      item.addEventListener('focus', () => select(item));
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(railTimer);
        railTimer = setTimeout(() => select(item), 70);
      });
      item.addEventListener('pointerleave', () => clearTimeout(railTimer));
      item.addEventListener('keydown', (e) => {
        let next = null;
        if (e.key === 'ArrowDown') next = items[(i + 1) % items.length];
        else if (e.key === 'ArrowUp') next = items[(i - 1 + items.length) % items.length];
        else if (e.key === 'Home') next = items[0];
        else if (e.key === 'End') next = items[items.length - 1];
        if (next) {
          e.preventDefault();
          select(next, { focus: true });
        }
      });
    });
  }

  // Drawer
  const drawer = root.querySelector('[data-ebm-drawer]');
  const burger = root.querySelector('[data-ebm-open]');
  if (drawer && burger && typeof drawer.showModal === 'function') {
    const shut = () => drawer.close();
    burger.addEventListener('click', () => {
      drawer.showModal();
      burger.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
    });
    drawer.addEventListener('close', () => {
      burger.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      burger.focus();
    });
    drawer.querySelector('[data-ebm-close]')?.addEventListener('click', shut);
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) shut();
    });
  }

  // Heights for sticky behaviour.
  const strip = group?.querySelector('.ebolt-bar-section');
  const measure = () => {
    if (!group) return;
    const stripH = strip ? strip.offsetHeight : 0;
    const groupH = group.offsetHeight;
    group.style.setProperty('--ebm-strip', `${stripH}px`);
    document.body.style.setProperty('--header-height', `${groupH - stripH}px`);
    document.body.style.setProperty('--header-group-height', `${groupH}px`);
    document.documentElement.style.setProperty('--eb-sticky-top', `${groupH - stripH}px`);
  };
  measure();
  if ('ResizeObserver' in window && group) new ResizeObserver(measure).observe(group);

  // Open/closed line under the phone number and in the drawer. Worked out
  // here in the store's time zone, because pages are cached. Hours come from
  // the section settings: "Mon-Thu 7:00-16:30, Fri 7:00-16:00" plus special
  // days, one per line: "2026-12-25 closed" or "2026-12-24 7:00-14:30".
  const hoursEls = [...document.querySelectorAll('[data-ebm-hours]')];
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const toMin = (t) => {
    const [h, m = '0'] = t.split(':');
    return Number(h) * 60 + Number(m);
  };
  const range = (s) => {
    const m = /(\d{1,2}(?::\d{2})?)\s*[-–]\s*(\d{1,2}(?::\d{2})?)/.exec(s);
    return m ? [toMin(m[1]), toMin(m[2])] : null;
  };
  const week = Array(7).fill(null);
  (root.dataset.ebmWeek || '').split(/[,;\n]+/).forEach((part) => {
    const m = /^\s*([a-z]{3})[a-z]*\s*(?:[-–]\s*([a-z]{3})[a-z]*)?\s+(.+)$/i.exec(part);
    if (!m) return;
    const from = DAYS.indexOf(m[1].toLowerCase());
    const to = m[2] ? DAYS.indexOf(m[2].toLowerCase()) : from;
    const hrs = range(m[3]);
    if (from < 0 || to < 0 || !hrs) return;
    for (let d = from; ; d = (d + 1) % 7) {
      week[d] = hrs;
      if (d === to) break;
    }
  });
  const special = {};
  (root.dataset.ebmSpecial || '').split(/[\n,;]+/).forEach((line) => {
    const m = /(\d{4}-\d{2}-\d{2})\s+(.+)/.exec(line);
    if (!m) return;
    if (/closed/i.test(m[2])) special[m[1]] = null;
    else if (range(m[2])) special[m[1]] = range(m[2]);
  });
  const clock = (min, ampm) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${ampm ? (h < 12 ? ' AM' : ' PM') : ''}`;
  };
  const storeNow = () => {
    const p = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
    }).formatToParts(new Date()).forEach(({ type, value }) => { p[type] = Number(value); });
    return { y: p.year, mo: p.month, d: p.day, min: (p.hour % 24) * 60 + p.minute };
  };
  const hoursOn = (now, offset) => {
    const day = new Date(Date.UTC(now.y, now.mo - 1, now.d + offset));
    const key = day.toISOString().slice(0, 10);
    return { dow: day.getUTCDay(), hrs: key in special ? special[key] : week[day.getUTCDay()] };
  };
  const storeStatus = () => {
    const now = storeNow();
    const today = hoursOn(now, 0).hrs;
    if (today && now.min >= today[0] && now.min < today[1]) return { open: true, text: `Open till ${clock(today[1])} today` };
    if (today && now.min < today[0]) return { open: false, text: `Opens at ${clock(today[0], true)} today` };
    for (let i = 1; i <= 14; i++) {
      const { dow, hrs } = hoursOn(now, i);
      if (hrs) return { open: false, text: `Closed, opens ${i === 1 ? 'tomorrow' : NAMES[dow]} ${clock(hrs[0], true)}` };
    }
    return null;
  };
  const paintHours = () => {
    let status = null;
    try { status = storeStatus(); } catch (e) { status = null; }
    hoursEls.forEach((el) => {
      el.hidden = !status;
      if (!status) return;
      el.textContent = status.text;
      el.toggleAttribute('data-open', status.open);
    });
  };
  if (hoursEls.length && week.some(Boolean)) {
    paintHours();
    setInterval(paintHours, 60000);
  }
})();
